import {
	LAST_OPENED_FILE_ID,
	METADATA_TYPE,
	REVIEW_DETAIL_DATE_FORMAT,
	SORT_ORDER,
	SOURCE_TYPE,
	STATUS,
	VIEWED_FILTER_KIND,
} from "./constants"
import {
	PdfSVG,
	XlsSVG,
	EmailSVG,
	PptSVG,
	HtmlSVG,
	TextSVG,
	EmailAttachSVG,
	DefaultFileSVG,
	DocSVG,
	ImageSVG,
	AudioSVG,
	VideoSVG,
} from "../components/icons/SourceType"
import _ from "lodash"
import moment from "moment"
import { sourceHelper } from "../helpers/sourceHelper"

/*{
	source: { kind: 110, data: ["ner", "pdf", "txt", "xls", "ppt"] },
	tags: { kind: 110, data: ["default_tag_1"] },
	tagged_date: { kind: 211, data: ["2023-03-23", "2023-03-24"] },
	commentator: { kind: 110, data: ["1234", "322323"] },
	from: { kind: 110, data: ["santosh@anzyz.com", "trshant@anzyz.com"] },
	to: { kind: 110, data: ["santosh@anzyz.com", "trshant@anzyz.com"] },
	view_count: { kind: 402, data: [0] },
}*/

const getCommentsCount = (comments) => {
	//based on show: true
	if (Array.isArray(comments)) {
		const filteredComments = comments.filter((comment) => comment.show)
		return filteredComments.length
	}
	return 0
}

const getTaggedDateFormat = (date) => {
	return `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}`
}

const clearLocalStorage = (removeList) => {
	if (!!removeList) {
		if (Array.isArray(removeList) && removeList.length()) {
			removeList.forEach((key) => {
				localStorage.removeItem(key)
			})
		} else if (typeof removeList === "string") {
			localStorage.removeItem(removeList)
		}
	} else {
		localStorage.clear()
		const mode = localStorage.getItem("mode")
		localStorage.clear()
		localStorage.setItem("mode", mode)
	}
	return true
}

const searchTermParse = (searchTearms) => {
	//received string as input and return an array of string
	if (searchTearms !== "") {
		const quotes_regExp = /"(.*?)"/g
		let terms_in_quotes = [...searchTearms.matchAll(quotes_regExp)].map((x) => x[1])
		let terms_outside = searchTearms
			.replaceAll(quotes_regExp, "")
			.trim()
			.split(" ")
			.filter((phrase) => phrase.length > 0)

		let sent_term_list_in_text = [...terms_outside, ...terms_in_quotes]
		sent_term_list_in_text = [...new Set(sent_term_list_in_text)]

		return sent_term_list_in_text
	} else {
		return []
	}
}

const searchTermDeparse = (searchTearms) => {
	//received array of string and return an string
	if (Array.isArray(searchTearms)) {
		let terms = ""
		/*
		//Now onwards will add Quotations mark on each term
		if (searchTearms.length === 1) {
			terms = searchTearms[0]
		} else {
			terms = searchTearms.map((term) => `"${term}"`).join(" ")
		}*/
		terms = searchTearms.map((term) => `"${term}"`).join(" ")
		return terms
	} else {
		return searchTearms
	}
}

const getSortedListByDate = (listToSort, sortOrder) => {
	return listToSort.sort((a, b) => {
		let b_value = new Date(b.tagged_date).getTime()
		let a_value = new Date(a.tagged_date).getTime()
		if (sortOrder === SORT_ORDER.ASCENDING) {
			return a_value - b_value
		} else {
			return b_value - a_value
		}
	})
}

const reSortArray = (refArr = [], targetData = []) => {
	/**
	 * refArr => [2, 1, 3]
	 * targetData => [{id:1...},{id:2...},{id:3...}]
	 * output => [{id:2...},{id:1...},{id:3...}]
	 **/
	targetData.sort(function (a, b) {
		return refArr.indexOf(a.id) - refArr.indexOf(b.id)
	})

	return targetData
}

const isDateValidated = (date) => {
	return (
		date.getFullYear() >= 1900 &&
		date.getFullYear() <= new Date().getFullYear() && //1900 is used as per LD-215 discussion
		date.getMonth() >= 0 &&
		date.getMonth() <= 11 &&
		date.getDate() >= 1 &&
		date.getDate() <= 31
	)
}

const deepCopy = (inObject) => {
	let outObject, value, key

	if (typeof inObject !== "object" || inObject === null) {
		return inObject
	}

	outObject = Array.isArray(inObject) ? [] : {}

	for (key in inObject) {
		value = inObject[key]
		outObject[key] = deepCopy(value)
	}

	return outObject
}

const getSourceSvg = (sourceType, attachments = []) => {
	switch (sourceType) {
		case SOURCE_TYPE.PDF:
			return <PdfSVG />
		case SOURCE_TYPE.DOC:
		case SOURCE_TYPE.DOCX:
			return <DocSVG />
		case SOURCE_TYPE.XLS:
		case SOURCE_TYPE.XLSX:
			return <XlsSVG />
		case SOURCE_TYPE.EMAIL:
			return attachments?.length > 0 ? <EmailAttachSVG /> : <EmailSVG />
		case SOURCE_TYPE.PPT:
			return <PptSVG />
		case SOURCE_TYPE.TXT:
			return <TextSVG />
		case SOURCE_TYPE.HTML:
			return <HtmlSVG />
		case SOURCE_TYPE.JPEG:
			return <ImageSVG />
		case SOURCE_TYPE.VIDEO:
			return <VideoSVG />
		case SOURCE_TYPE.AUDIO:
			return <AudioSVG />
		default:
			return <DefaultFileSVG />
	}
}

const getStatusColor = (status, theme) => {
	switch (status) {
		case STATUS.IN_PROGRESS:
		case STATUS.COPYING:
		case STATUS.DETECTING_DUPLICATE_FILES:
		case STATUS.TEXT_EXTRACTION:
		case STATUS.PII_EXTRACTION:
		case STATUS.COPIED:
		case STATUS.TEXT_EXTRACTED:
			return { color: `${theme.palette.status.in_progress}`, background: `${theme.palette.status.in_progress_background}` }
		case STATUS.SUCCEEDED:
		case STATUS.SUCCESSFUL:
		case STATUS.DONE:
		case STATUS.ACTIVE:
			return { color: `${theme.palette.status.succeeded}`, background: `${theme.palette.status.succeeded_background}` }
		case STATUS.FAILED:
		case STATUS.UNSUCCESSFUL:
		case STATUS.PARTIALLY_SUCCESSFUL:
			return { color: `${theme.palette.status.failed}`, background: `${theme.palette.status.failed_background}` }
		case STATUS.CREATED:
		case STATUS.QUEUED:
		case STATUS.MARKED:
		case STATUS.MAINTENANCE:
		default:
			return { color: `${theme.palette.status.created}`, background: `${theme.palette.status.created_background}` }
	}
}

const parsePayload = (data) => {
	let payload = deepCopy(data)

	if (payload.source.data.length === 0) {
		delete payload.source
	}

	if (payload.tags.data.length === 0) {
		delete payload.tags
	}

	if (payload.tagged_date.data.length === 0) {
		delete payload.tagged_date
	}

	if (payload.commentator.data.length === 0) {
		delete payload.commentator
	}

	if (payload.from.data.length === 0) {
		delete payload.from
	}

	if (payload.to.data.length === 0) {
		delete payload.to
	}

	if (payload.view_count.kind === VIEWED_FILTER_KIND.default) {
		delete payload.view_count
	} else {
		if (_.has(payload, "view_count")) {
			payload = { ...payload, view_count: { ...payload.view_count, data: payload?.view_count?.kind > 0 ? true : undefined } }
		}
	}

	if (payload?.subject?.length === 0) {
		delete payload.subject
	}

	if (payload?.has_attachment?.length === 0) {
		delete payload.has_attachment
	}

	return payload
}

const depasrse = () => {}

const getFormattedTime = (millisecondTime) => {
	if (millisecondTime > 0) {
		return new Date(millisecondTime).toISOString().slice(11, 19)
	} else if (millisecondTime === 0) {
		return 0
	} else {
		return "-"
	}
}

const validateEmail = (email) => {
	const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
	return email?.match(regex) ? true : false
}

const filterValues = {
	source: "File Types",
	tags: "Tags",
	tagged_date: "Date Range",
	comments_by: "Comments By",
	from: "Sender",
	to: "Receiver",
	viewed: "View",
}
const convertFilterToString = (filters = {}) => {
	let filtersStringArr = []
	if (!_.isEmpty(filters)) {
		const keys = Object.keys(filters)
		if (keys?.length) {
			for (const str of keys) {
				if (filterValues[str]) {
					filtersStringArr.push(filterValues[str])
				}
			}
		}
	}

	return filtersStringArr.length ? filtersStringArr.join(", ") : ""
}

const getFilterValue = (currentfilter, filters) => {
	switch (currentfilter) {
		case "source":
			return filters[currentfilter].join(",")
			break
		case "tags":
			return filters[currentfilter].join(",")
			break
		case "tagged_date":
			return `${moment(filters[currentfilter]?.start_date).format(REVIEW_DETAIL_DATE_FORMAT)} - ${moment(
				filters[currentfilter]?.end_date
			).format(REVIEW_DETAIL_DATE_FORMAT)}`
			break
		case "comments_by":
			return filters[currentfilter].join(",")
			break
		case "viewed":
			return filters[currentfilter] === true ? "Viewed" : "Not Viewed"
			break
		case "from":
			return filters[currentfilter]?.join(", ")
			break
		case "to":
			return filters[currentfilter].join(", ")
			break
		default:
			return ""
			break
	}
}

const convertFilterToObject = (filters = {}) => {
	let filtersObject = {}
	if (!_.isEmpty(filters)) {
		const keys = Object.keys(filters)
		if (keys?.length) {
			for (const str of keys) {
				if (filterValues[str]) {
					filtersObject[filterValues[str]] = getFilterValue(str, filters)
				}
			}
		}
	}

	return filtersObject
}

const getConceptsById = (concepts = [], ids = [], returnArr = false) => {
	let usedConceptsStr = []
	if (concepts.length && ids?.length) {
		usedConceptsStr = concepts?.filter((concept) => ids?.includes(concept?.id))
	}
	return usedConceptsStr
}

const getSearchNameById = (searchList = [], searchId, allDocumentsStr) => {
	let usedSearch = []
	if (searchList?.length) {
		usedSearch = searchList?.filter((search) => searchId === search?.id).map((search) => search?.name)
	}
	return usedSearch.length ? usedSearch.join(", ") : allDocumentsStr
}

// const formatFileSize = (bytes) => {
// 	if (bytes == 0) return "0 Bytes"
// 	var k = 1000,
// 		dm = 2,
// 		sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
// 		i = Math.floor(Math.log(bytes) / Math.log(k))
// 	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
// }

const formatFileSize = (bytes, dp = 1) => {
	const thresh = 1024

	if (Math.abs(bytes) < thresh) {
		return bytes + " B"
	}

	const units = ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
	let u = -1
	const r = 10 ** dp

	do {
		bytes /= thresh
		++u
	} while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

	return bytes.toFixed(dp) + " " + units[u]
}

const removeFileIdFromLocalStorage = () => {
	localStorage?.removeItem(LAST_OPENED_FILE_ID)
}

function buildQueryString(payload) {
	const obj = verifyPayload(payload)
	const queryString = Object.keys(obj)
		.map((key) => {
			if (typeof obj[key] === "object") {
				return `${key}=${encodeURIComponent(JSON.stringify(obj[key]))}`
			} else {
				return `${key}=${encodeURIComponent(obj[key])}`
			}
		})
		.join("&")
	//history.push(`${location.pathname}?${queryString}`)
	return queryString
}

function updateQueryString(newKeys, removeKeys = []) {
	// Get the current query string
	const currentQueryString = getQueryParams()

	// Merge the payload object with the current query parameters
	//nested working- not sure
	const updatedParams = { ...currentQueryString, ...newKeys }

	//remove keys if we received
	const sanitizedpayload = sanitizeKeys(updatedParams, removeKeys)

	// Construct a new query string from the updated parameters
	const updatedQueryString = buildQueryString(removeKeys?.length ? sanitizedpayload : updatedParams)

	// Return the updated query string
	return updatedQueryString
}

function getQueryParams() {
	const obj = {}
	const params = new URLSearchParams(location.search)
	params.forEach((value, key) => {
		try {
			obj[key] = JSON.parse(decodeURIComponent(value))
		} catch (error) {
			obj[key] = decodeURIComponent(value)
		}
	})
	return obj
}

//oldValue:NewValue
const fieldsMapping = {
	title: "title",
	tagged_date: "tagged_date",
	view_count: "viewed",
	comments_count: "number_of_comments",
	from: "email_from",
	file_id: "file_id",
}

const getAttachmentValue = (arr) => {
	//this will return true or false
	const hasId0 = arr.includes(0)
	const hasId1 = arr.includes(1)

	if (hasId0 && hasId1) {
		return true
	} else {
		return hasId1
	}
}

function payloadParsing(oldPayload, isGraph = false) {
	const newPayload = {
		terms: oldPayload?.text || [],
		concept_ids: oldPayload?.categories || [],
		treat_like_concept: oldPayload?.treat_like_concept || false,
		remove_duplicates: !!oldPayload?.remove_duplicates,
		count_only: false, //return no of docs
		basic_info_only: isGraph ? true : false, //for graph

		//following payload need discussion
		exclude_terms: [],
		exclude_concept_ids: [],
		logical_or: oldPayload?.logical_or || false,
		page_number: Math.floor((oldPayload?.skip_count + oldPayload?.return_count) / oldPayload?.return_count) || 1,
		page_size: isGraph ? 50000 : oldPayload?.return_count || 10,
		filters: {
			tagged_date:
				(oldPayload.tagged_date &&
					oldPayload.tagged_date.data.length && {
						start_date: oldPayload.tagged_date.data[0],
						end_date: oldPayload.tagged_date.data[1],
					}) ||
				undefined,
			source: (oldPayload.source && oldPayload.source.data) || undefined,
			comments_by: (oldPayload.commentator && oldPayload.commentator.data) || undefined,
			tags: (oldPayload.tags && oldPayload.tags.data) || undefined,
			from: (oldPayload.from && oldPayload.from.data) || undefined,
			to: (oldPayload.to && oldPayload.to.data) || undefined,
			viewed: _.has(oldPayload, "view_count") ? (oldPayload.view_count.kind > 0 ? true : false) : undefined,
			subject: _.has(oldPayload, "subject") ? oldPayload.subject : undefined,
			has_attachment:
				oldPayload?.has_attachment && oldPayload?.has_attachment?.length > 0
					? getAttachmentValue(oldPayload?.has_attachment)
					: undefined,
		},
		sort:
			(oldPayload.sort && { field: fieldsMapping[oldPayload.sort.field], order: oldPayload.sort.ordering === 1 ? "asc" : "desc" }) ||
			undefined,
		review_id: oldPayload?.additional_params?.review_id || undefined,
		is_case_admin: oldPayload?.additional_params?.is_case_admin || undefined,
	}

	/**
	 * have 2 options in has_attachment
	 * 1. with attachment
	 * 2. without attachment
	 * if both are selected then we are not sending this key, as payload required true and false only
	 */
	if (oldPayload?.has_attachment?.length === 2) {
		delete newPayload.filters.has_attachment
	}

	return newPayload
}

const verifyPayload = (payload) => {
	const verifiedPayload = {
		...payload,
		text: payload?.text || [],
		categories: payload?.categories || [],

		treat_like_concept: payload?.treat_like_concept || false,
		//remove_duplicates: payload?.remove_duplicates,

		skip_count: payload?.skip_count || 0,
		return_count: payload?.return_count || 10,
	}

	return verifiedPayload
}

//reviews API review_files response mapping
function convertReviewsArrayToMap(reviewFiles = []) {
	const result = {}
	reviewFiles.forEach((file) => {
		const fileId = file.file_id
		const { reviewed, user_id } = file
		result[fileId] = { reviewed, user_id }
	})
	return result
}

//this will add new files in the user asignee
function updateReviewAsignee(data = {}, newData = {}) {
	const updatedData = _.cloneDeep(data)

	Object.keys(newData).forEach((key) => {
		if (updatedData.hasOwnProperty(key)) {
			const existingValues = updatedData[key]
			const newValues = newData[key]
			const mergedValues = [...new Set(existingValues.concat(newValues))]
			updatedData[key] = mergedValues
		} else {
			updatedData[key] = newData[key] // Add new key-value pair
		}
	})

	return updatedData
}

function updateReviewAsigneeMap(data = new Map(), newData = new Map()) {
	const updatedData = new Map(data)

	newData.forEach((userId, fileId) => {
		if (updatedData.has(fileId)) {
			const existingUserId = updatedData.get(fileId)
			if (existingUserId !== userId) {
				console.log(`Warning: fileId '${fileId}' already exists with userId '${existingUserId}'. Skipping update.`)
			}
		} else {
			updatedData.set(fileId, userId)
		}
	})

	return updatedData
}

//this is an old code we move it from document.service to helper function
function getTimelinePayload(
	positive_terms,
	negative_terms,
	filter = [],
	categories = [],
	treatLikeConcept = false,
	return_count = 10,
	cancelToken = null,
	sourceApi,
	skipCount = 0,
	sort = {},
	filters = {},
	page_context = "",
	skip_audit = false,
	search_id = "",
	remove_duplicates = true,
	selecteGraphdDateRange = {},
	restrict_file_ids,
	review_id = "",
	is_case_admin = false,
	subjectText
) {
	try {
		let filterResume = []
		let dateRange = []
		const taggedDate = { kind: METADATA_TYPE.FROM_TO_DATE_ARRAY, data: dateRange }
		const filterSources = { kind: METADATA_TYPE.STRING_ARRAY, data: filterResume }
		let dateFilter = { from: "", to: "" }
		let filtersActive = 0
		if (!Array.isArray(filter)) {
			if (sourceApi.hasOwnProperty("sources")) {
				sourceApi.sources.forEach(function (key) {
					if (filter[key]) {
						if (filter[key] === true) {
							filterResume.push(key)
							filtersActive++
						}
					} else {
						let otherKeys = sourceHelper.getKeyArrayHumanValue(sourceHelper.getHumanValue(key))
						otherKeys.forEach(function (keyAux) {
							if (filter.hasOwnProperty(keyAux)) {
								if (filter[keyAux] === true) {
									filterResume.push(key)
									filtersActive++
								}
							}
						})
					}
				})
			}

			if (filter.toDate !== "") {
				dateFilter.to = new Date(filter.toDate)
				dateFilter.to = getTaggedDateFormat(dateFilter.to)
				if (dateFilter.to !== "NaN-aN-aN") dateRange[1] = dateFilter.to
			}
			if (filter.fromDate !== "") {
				dateFilter.from = new Date(filter.fromDate)
				dateFilter.from = getTaggedDateFormat(dateFilter.from)
				if (dateFilter.from !== "NaN-aN-aN") dateRange[0] = dateFilter.from
			}
		}

		let positiveInput = ""

		if (positive_terms.length > 0) {
			positiveInput = positive_terms.join(" ")
		}

		let negativeInput = ""
		if (negative_terms.length > 0) {
			negativeInput = negative_terms
		}

		const input = {
			text: positive_terms,
			neg_text: negativeInput,
			categories: categories,
			return_count: return_count,
			skip_count: skipCount,
			dynamic_search: 0,
			suppress_content: false,
			treat_like_concept: treatLikeConcept,
			remove_duplicates: remove_duplicates,
			mode: "FILE",
			...filters,
		}

		let additional_params = {}
		if (skip_audit) {
			additional_params = { ...additional_params, skip_audit }
		}
		if (page_context) {
			additional_params = { ...additional_params, page_context }
		}

		if (search_id) {
			additional_params = { ...additional_params, search_id }
		}

		if (review_id) {
			additional_params = { ...additional_params, review_id, is_case_admin }
		}

		if (selecteGraphdDateRange?.selected) {
			additional_params = { ...additional_params, graphDateRange: selecteGraphdDateRange }
		}
		if (!_.isEmpty(additional_params)) {
			input["additional_params"] = { ...additional_params }
		}

		if (restrict_file_ids?.length > 0) {
			input["restrict_file_ids"] = restrict_file_ids
		}

		if (!!dateRange.length) {
			if (dateRange[0] === undefined) dateRange[0] = "1900-01-01" //To do: Need to fix it to get the oldest doc date, for now keeping it '1900-01-01' as per LD-215
			if (dateRange[1] === undefined) dateRange[1] = getTaggedDateFormat(new Date())
			input.tagged_date = taggedDate
		}

		if (subjectText) {
			input["subject"] = subjectText
		}

		if (Object.keys(sort).length > 0) {
			input.sort = sort
		}

		return input
	} catch (error) {
		console.warn("Error in timelinePayload", error)
	}
}
//const keysToRemove = ["shared_search_id", "your_search_id"]
function sanitizeKeys(payload, keysToRemove) {
	for (const key in payload) {
		if (Object.prototype.hasOwnProperty.call(payload, key)) {
			if (typeof payload[key] === "object" && payload[key] !== null) {
				payload[key] = sanitizeKeys(payload[key], keysToRemove)
			}

			if (keysToRemove.includes(key)) {
				delete payload[key]
			}
		}
	}
	return payload
}

function addGraphRecord(graph, event) {
	const graphdata = _.cloneDeep(graph)
	const startDate = event.date
	const endDate = event.endDate
	const graphStartDate = graphdata[0][0]
	const graphEndDate = graphdata[graphdata.length - 1][0]

	// Check if event is within the range of graphdata
	if (startDate >= graphStartDate && endDate <= graphEndDate) {
		return graphdata
	} else {
		// Find the index where startDate falls in graphdata
		let startIndex = graphdata.findIndex((entry) => entry[0] >= startDate)

		// Find the index where endDate falls in graphdata
		let endIndex = graphdata.findIndex((entry) => entry[0] >= endDate)

		// If endDate is not found in graphdata, set it to the last index
		//if (endIndex === -1) endIndex = graphdata.length

		// if (startIndex === -1 && endIndex === -1) {
		// 	// If both startDate and endDate are not in range, add records for both
		// 	graphdata.push([startDate, 0], [endDate, 0])
		// } else
		if (startIndex === -1 || endIndex === -1) {
			// If only one of startDate or endDate is not in range, add that specific date record
			if (startIndex === -1) graphdata.push([startDate, 0])
			if (endIndex === -1) graphdata.push([endDate, 0])
		} else {
			graphdata.splice(startIndex, 0, [startDate, 0])
			graphdata.splice(endIndex + 1, 0, [endDate, 0])
		}
	}

	return graphdata
}

function addGraphRecord_old(graphData, event) {
	const graph = _.cloneDeep(graphData)

	const eventDate = parseInt(event.date)
	const eventEndDate = parseInt(event.endDate)

	const index = binarySearch(graph, eventDate)

	if (index === -1 || graph[index][0] !== eventDate) {
		// If event date not found, add a new record
		graph.splice(index + 1, 0, [eventDate, 0])

		if (index + 1 === graphData.length) {
			/**
			 * if we are placing event at end of the graph then we should consider the last date of event as well
			 * otherwise if event is more than 1 day it still showing the single day event,
			 * to resove this we need to add the end date entry as well in the graph
			 */
			graph.splice(index + 2, 0, [eventEndDate, 0])
		}
	}

	return graph
}

function binarySearch(arr, target) {
	let leftIndex = 0
	let rightIndex = arr.length - 1

	while (leftIndex <= rightIndex) {
		const mid = Math.floor((leftIndex + rightIndex) / 2)
		if (arr[mid][0] === target) {
			return mid // Found the target
		} else if (arr[mid][0] < target) {
			leftIndex = mid + 1 // Continue searching in the right half
		} else {
			rightIndex = mid - 1 // Continue searching in the left half
		}
	}

	return rightIndex // Return the index of the last element smaller than target
}

function removeGraphRecord(graphData, event) {
	const graph = _.cloneDeep(graphData)
	if (!_.isEmpty(event)) {
		const indexToRemoveStartDate = graph.findIndex((record) => record[0] === event.date && record[1] === 0)
		if (indexToRemoveStartDate !== -1) {
			graph.splice(indexToRemoveStartDate, 1)
		}

		const indexToRemoveEndDate = graph.findIndex((record) => record[0] === event.endDate && record[1] === 0)
		if (indexToRemoveEndDate !== -1) {
			graph.splice(indexToRemoveEndDate, 1)
		}
	}
	return graph
}

function checkDateInRange(graphData, event) {
	const { date, endDate } = event

	// Convert event dates to milliseconds
	const startDateMS = new Date(date).getTime()
	const endDateMS = new Date(endDate).getTime()

	// Check if the graphData contains data for the specified date range
	const withinRange = graphData.some((data) => {
		const graphDateMS = data[0]
		return graphDateMS >= startDateMS && graphDateMS <= endDateMS
	})

	// Check if both date and endDate are within the range
	if (withinRange) {
		return { date: true, endDate: true }
	} else {
		// Check individually if date and endDate are within the range
		const dateInRange = graphData.some((data) => {
			const graphDateMS = data[0]
			return graphDateMS >= startDateMS
		})

		const endDateInRange = graphData.some((data) => {
			const graphDateMS = data[0]
			return graphDateMS <= endDateMS
		})

		return { date: dateInRange, endDate: endDateInRange }
	}
}

function checkToShowNativeFormat(metadeta) {
	if (metadeta?.source === "pdf" || metadeta?.native_pdf_status === "Done") {
		if (metadeta?.source === "img") {
			return false
		}
		return true
	}
	return false
}

export {
	getCommentsCount,
	getTaggedDateFormat,
	clearLocalStorage,
	searchTermParse,
	searchTermDeparse,
	getSortedListByDate,
	reSortArray,
	isDateValidated,
	parsePayload,
	depasrse,
	deepCopy,
	getSourceSvg,
	getStatusColor,
	getFormattedTime,
	validateEmail,
	convertFilterToString,
	getConceptsById,
	getSearchNameById,
	convertFilterToObject,
	formatFileSize,
	removeFileIdFromLocalStorage,
	buildQueryString,
	updateQueryString,
	getQueryParams,
	payloadParsing,
	verifyPayload,
	convertReviewsArrayToMap,
	updateReviewAsignee,
	updateReviewAsigneeMap,
	getTimelinePayload,
	sanitizeKeys,
	addGraphRecord,
	removeGraphRecord,
	checkToShowNativeFormat,
	binarySearch,
}
